import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../Helpers/Utils";
import BackButton from "../../Components/BackButton";
import Loading from "../../Components/Loading";
import Timeline from "../../Components/Timeline";
import VariantFormModal from "./VariantFormModal";

const DetailRow = ({ label, value, capitalize = false }) => (
    <div className="row align-items-sm-center mb-2">
        <label className="fw-medium col-sm-6 col-form-label">{label}</label>
        <div className={`col-sm-6 d-flex align-items-center ${capitalize ? "text-capitalize" : ""}`}>
            {value || value === 0 ? value : "-"}
        </div>
    </div>
);

const VariantDetail = () => {
    const { variantId } = useParams();
    const [variant, setVariant] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingStatus, setTogglingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [variantResponse, logResponse] = await Promise.all([
            Handler({ method: "GET", url: `/admin/vehicle/variant/${variantId}` }),
            Handler({ method: "GET", url: `/admin/vehicle/variant/${variantId}/log`, params: { limit: 20 } }),
        ]);
        setVariant(variantResponse?.success ? variantResponse?.data?.data : null);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setLoading(false);
    }, [variantId]);

    useEffect(() => { load(); }, [load]);

    const toggleStatus = async () => {
        setTogglingStatus(true);
        const response = await Handler({ method: "patch", url: `/admin/vehicle/variant/${variantId}/status`, data: { status: !variant?.status } });
        if (response?.success) {
            toast.success(response?.data?.message || "Status updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update status"));
        }
        setTogglingStatus(false);
    };

    if (loading) {
        return (
            <Loading />
        );
    }

    if (!variant) {
        return (
            <div className="container-fluid">
                <BackButton name="to variants" url="/variant" className="mb-3" />
                <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-body text-center py-5">
                        <p className="text-muted mb-0">Variant not found.</p>
                    </div>
                </div>
            </div>
        );
    }

    const isActive = variant.status === true;

    return (
        <React.Fragment>
            <div className="container-fluid">
                <BackButton name="to variants" url="/variant" className="mb-3" />

                <div className="row g-4">
                    <div className="col-12 col-xl-8">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-2 justify-content-between flex-wrap gap-2">
                                    <h3 className="text-primary text-capitalize mb-0">
                                        {(variant.brand?.brand || "") + " - " + (variant.model?.modelName || "") + " - " + (variant.variantName || "")}
                                    </h3>
                                    <div className="text-end d-flex flex-wrap gap-2">
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={toggleStatus} disabled={togglingStatus}>
                                            {togglingStatus ? "Updating..." : isActive ? "Deactivate" : "Activate"}
                                        </button>
                                        <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowEditModal(true)}>Edit variant</button>
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
                                    <span className={`badge rounded-pill ${isActive ? "text-bg-success" : "text-bg-danger"}`}>
                                        <i className={`bi ${isActive ? "bi-check-circle" : "bi-x-circle"}`}></i>
                                        {isActive ? "Active" : "Inactive"}
                                    </span>
                                </div>

                                <div className="row">
                                    <div className="col-xl-6">
                                        <DetailRow label="Variant Id" value={variant.variantId} />
                                        <DetailRow label="Variant Name" value={variant.variantName} capitalize />
                                        <DetailRow label="Brand Id" value={variant.brand?.brandId} />
                                        <DetailRow label="Brand Name" value={variant.brand?.brand} capitalize />
                                        <DetailRow label="Model Id" value={variant.model?.modelId} />
                                    </div>
                                    <div className="col-xl-6">
                                        <DetailRow label="Model Name" value={variant.model?.modelName} capitalize />
                                        <DetailRow label="Segment" value={variant.model?.segment} capitalize />
                                        <DetailRow label="Sub Segment" value={variant.model?.subSegment} capitalize />
                                        <DetailRow label={`${variant.brand?.brandType === "bikes" ? "Bikes" : "Cars"} on this variant`} value={variant.unitCount}/>
                                        <DetailRow label="Created" value={formatDateTime(variant.createdAt)} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-xl-4">
                        <Timeline userLog={logs} />
                    </div>
                </div>
            </div>

            {showEditModal ? (
                <VariantFormModal
                    mode="edit"
                    modelId={variant.modelId}
                    variant={variant}
                    onClose={() => setShowEditModal(false)}
                    onSaved={() => { setShowEditModal(false); load(); }}
                />
            ) : ""}
        </React.Fragment>
    );
};

export default VariantDetail;
